import { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import type { Quarter } from './periods';
import { formatCurrency } from './CurrencyDisplay';
import { getYearColor } from './QuarterHeader';
import { useTableSettings } from './TableSettingsContext';
import { getForecastContext } from './periods';

interface EditableCellProps {
  value: number;
  quarter: Quarter;
  onChange: (value: number) => void;
  className?: string;
  disabled?: boolean;
  /**
   * Open the editor on a single click, as an input box does. Off by default: v1 opens it on
   * a double-click and keeps doing so. v2 turns it on so its editable figures behave like
   * Baseline Accounting's inputs, which are fields you click into and type.
   */
  editOnClick?: boolean;
}

export function EditableCell({
  value,
  quarter,
  onChange,
  className,
  disabled = false,
  editOnClick = false,
}: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { settings } = useTableSettings();
  const fiscalYears = settings ? getForecastContext(settings).fiscalYears : [];

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = useCallback(() => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(value === 0 ? '' : String(value));
  }, [value, disabled]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    const parsed = parseFloat(editValue);
    if (!isNaN(parsed)) {
      onChange(parsed);
    } else if (editValue.trim() === '') {
      onChange(0);
    }
  }, [editValue, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleBlur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
      } else if (e.key === 'Tab') {
        handleBlur();
      }
    },
    [handleBlur],
  );

  if (isEditing) {
    return (
      <td className={clsx('px-1 py-0 border-r border-b border-gray-200', className)}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-full px-2 py-1 text-right text-sm tabular-nums outline-none ring-2 ring-blue-500 rounded-sm bg-white"
        />
      </td>
    );
  }

  const yearColor = getYearColor(quarter, fiscalYears);

  return (
    <td
      title={disabled ? 'Historical (actual) value — read-only' : undefined}
      className={clsx(
        'px-3 py-1.5 text-right text-sm tabular-nums border-r border-b border-gray-200',
        disabled
          ? 'cursor-default bg-amber-50/60 italic text-gray-600'
          : clsx(
              'cursor-pointer',
              'hover:bg-blue-50 hover:ring-1 hover:ring-blue-300 hover:ring-inset',
              value === 0 ? 'text-gray-400' : value < 0 ? 'text-red-600' : 'text-gray-900',
              yearColor,
            ),
        className,
      )}
      onDoubleClick={handleDoubleClick}
      onClick={editOnClick ? handleDoubleClick : undefined}
    >
      {value === 0 ? '-' : formatCurrency(value)}
    </td>
  );
}
